import {Radio} from 'acud';
import Form from 'acud/es/form';
import track from '@baidu/bce-track';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import duration from 'dayjs/plugin/duration';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import minMax from 'dayjs/plugin/minMax';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import isBetween from 'dayjs/plugin/isBetween';
import 'dayjs/locale/zh-cn';

// i18n 加载完成前不要引入需要翻译的内容，或者使用 lazy 加载
// 例: const api = await import('@/api');
import {setConstants, setFlags, setRegion, setSession, FrameworkConstants} from '@/common/framework';
import {initI18n} from '@/common/i18n';
import {Service} from '@/common/interface';
import {getEopAccountId} from '@/common/user';


export const initConsoleFramework = () => Promise.all([
    window.framework.boot().then(async initData => {
        // 保存 framework 初始化常量
        setConstants(initData?.constants);
        setFlags(initData?.flag);
        setRegion(initData?.region);
        setSession(initData?.session);
    }),
    initI18n(),
]);

export const initEopConstants = async () => {
    const api = await import('@/api');
    await Promise.all([
        getEopAccountId()
            ? api.eopApi.post<FrameworkConstants>('/api/crm2/stat/user/common/constants',
                {accountId: getEopAccountId()})
            : Promise.resolve({} as FrameworkConstants),
        api.eopApi.post<Service[]>('/api/crm2/system/products', {}),
        getEopAccountId()
            ? api.eopApi.post('/api/sale/coupon/get_account_area', {accountIdList: [getEopAccountId()]})
                .then<boolean>(res => res.isAllArea).catch(() => false)
            : Promise.resolve(false),
    ])
        .then(async ([constants, serviceList, isEopOverseas]) => {
            const SERVICES: Record<string, Service> = {};
            serviceList?.forEach(item => {
                SERVICES[item.serviceType.toUpperCase()] = item;
            });
            constants.SERVICE_TYPE = SERVICES;
            setConstants(constants);
            setSession({
                isAgent: () => constants.agentInfo?.userType === 'AGENT',
                isAgentUser: () => constants.agentInfo?.userType === 'AGENTUSER',
            });
            const overseasConstants = await import('@/common/overseas');
            overseasConstants.setIsEopOverseas(isEopOverseas);
        })
        .catch();
};

export const initPartnerConstants = async () => {
    const api = await import('@/api');
    const constants = await api.billingApi.post<FrameworkConstants>('/api/system/constants/v3');
    const SERVICES: Record<string, Service> = {};
    constants.services?.forEach(item => {
        SERVICES[item.serviceType.toUpperCase()] = item;
    });
    constants.SERVICE_TYPE = SERVICES;
    setConstants(constants);
    // 数据有误，但目前不影响
    setSession({
        isAgent: () => false,
        isAgentUser: () => true,
    });
    initI18n();
};

export const initDayJs = () => {
    dayjs.locale('zh-cn');
    dayjs.extend(utc);
    dayjs.extend(timezone);
    dayjs.extend(quarterOfYear);
    dayjs.extend(duration);
    dayjs.extend(advancedFormat);
    dayjs.extend(minMax);
    dayjs.extend(weekOfYear);
    dayjs.extend(weekYear);
    dayjs.extend(isBetween);
};

export const initComponentConfig = () => {
    (Form as any).defaultProps = {
        ...(Form as any).defaultProps,
        inputMaxWidth: 'none',
        labelAlign: 'left',
    };

    // 关闭 acud Radio.Button 默认 8 字符的省略截断（详见 RadioButton.js 的 limit 行为）
    (Radio.Button as any).defaultProps = {
        ...(Radio.Button as any).defaultProps,
        limit: 0,
    };
};

export const initConsoleConfig = () => {
    track.install({
        uploadUrl: '/api/log/csi',
        autoTrack: true,
        limit: 1,
    });

    initComponentConfig();
};

export const initMobileFramework = async () => {
    const api = await import('@/api');
    const result = await api.billingApi.post<FrameworkConstants>('/api/system/constants/v3');
    const SERVICES: Record<string, Service> = {};
    result.services?.forEach((item: Service) => {
        SERVICES[item.serviceType.toUpperCase()] = item;
    });
    result.SERVICE_TYPE = SERVICES;
    setConstants(result);
};
